import { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Building2, Stethoscope, ShieldCheck, ArrowRight, Sparkles, 
  Brain, ShoppingCart, Wallet, Crown, Package, Upload, MapPin,
  DollarSign, BarChart3, RefreshCw, ChevronDown,
  Heart, Activity, ClipboardList, Store, Truck, PieChart, Shield,
  Users, MessageSquare, X, Zap, Bot, Star, FileText
} from 'lucide-react';
import DownloadAppButton from '../components/DownloadAppButton';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const portals = [
  {
    id: 'doctor',
    title: 'Doctor Portal',
    tagline: 'Complete Clinic Management',
    description: 'AI-powered practice management for single-doctor clinics',
    icon: Stethoscope,
    bgGradient: 'from-teal-500 to-emerald-600',
    borderClass: 'border-teal-200 hover:border-teal-400',
    accent: 'teal',
    route: '/doctor/login',
    demo: 'doctor@clinic.com / clinic123',
    highlights: [
      { icon: Brain, label: 'AI Scribe', isNew: true },
      { icon: Bot, label: '1Clin AI', isNew: true },
      { icon: ShoppingCart, label: 'B2B Marketplace' },
      { icon: Crown, label: 'Subscriptions' },
    ],
    featureGroups: [
      { category: 'Clinic Operations', icon: ClipboardList, features: ['Dashboard', 'Appointments', 'Patients', 'Clinical Visits', 'Billing'] },
      { category: 'AI & Automation', icon: Brain, features: ['SOAP Notes', 'Voice Scribe', '1Clin AI Chat', 'WhatsApp', 'Auto-Renew'] },
      { category: 'B2B Marketplace', icon: Store, features: ['Lab Tests', 'Medicines', 'Equipment', 'Reviews & Ratings'] },
      { category: 'Finance & More', icon: Wallet, features: ['Wallet', 'Expenses', 'Marketing & Growth', 'MR Visitors'] },
    ]
  },
  {
    id: 'patient',
    title: 'Patient Portal',
    tagline: 'Your Health Hub',
    description: 'Access health records, order medicines, and book consultations',
    icon: Heart,
    bgGradient: 'from-rose-500 to-pink-600',
    borderClass: 'border-rose-200 hover:border-rose-400',
    accent: 'rose',
    route: '/patient/login',
    demo: 'OTP Login (Use phone)',
    highlights: [
      { icon: ShoppingCart, label: 'Marketplace', isNew: true },
      { icon: MessageSquare, label: 'Consultations' },
      { icon: Wallet, label: 'Wallet' },
    ],
    featureGroups: [
      { category: 'Health Records', icon: Activity, features: ['Visits', 'Prescriptions', 'Lab Reports', 'Bills'] },
      { category: 'Marketplace', icon: ShoppingCart, features: ['Medicines', 'Lab Tests', 'Wallet Pay', 'Orders'] },
      { category: 'Consultations', icon: MessageSquare, features: ['Video Calls', 'Appointments', 'Doctor Chat'] },
      { category: 'Account', icon: Users, features: ['Profile', 'Wallet', 'Referrals', 'Subscription'] },
    ]
  },
  {
    id: 'vendor',
    title: 'Vendor Portal',
    tagline: 'Labs \u2022 Pharma \u2022 Equipment',
    description: 'Manage products, orders, and connect with clinics',
    icon: Building2,
    bgGradient: 'from-blue-500 to-indigo-600',
    borderClass: 'border-blue-200 hover:border-blue-400',
    accent: 'blue',
    route: '/vendor/login',
    demo: 'lab@citydiag.com / lab123',
    highlights: [
      { icon: MapPin, label: 'MR Tracking', isNew: true },
      { icon: DollarSign, label: 'Earnings' },
      { icon: Upload, label: 'Bulk Import' },
    ],
    featureGroups: [
      { category: 'Catalog', icon: Package, features: ['Products', 'Excel Import', 'Photos', 'Price Tiers', 'Expiry'] },
      { category: 'Orders', icon: Truck, features: ['Tracking', 'Status', 'Invoices', 'Bulk Upload'] },
      { category: 'MR Management', icon: MapPin, features: ['Live Tracking', 'GPS Verify', 'Analytics', 'Top Clinics'] },
      { category: 'Earnings', icon: DollarSign, features: ['Referral Charges', 'Bank Setup', 'Payouts', 'Coupons'] },
    ]
  },
  {
    id: 'admin',
    title: 'Super Admin',
    tagline: 'Platform Control Center',
    description: 'Complete platform administration and analytics',
    icon: ShieldCheck,
    bgGradient: 'from-violet-500 to-fuchsia-600',
    borderClass: 'border-violet-200 hover:border-violet-400',
    accent: 'violet',
    route: '/admin/login',
    demo: 'superadmin@clinic.com / super123',
    highlights: [
      { icon: RefreshCw, label: 'Auto-Renewal', isNew: true },
      { icon: Zap, label: 'Offers', isNew: true },
      { icon: PieChart, label: 'Profitability' },
    ],
    featureGroups: [
      { category: 'Users', icon: Users, features: ['Vendors', 'Doctors', 'Patients', 'Subscriptions'] },
      { category: 'Subscriptions', icon: Crown, features: ['Plans', 'Offers & Trials', 'Promo Codes', 'Auto-Renew'] },
      { category: 'Finance', icon: Wallet, features: ['Wallets', 'Referral Charges', 'Payouts', 'Credits'] },
      { category: 'Analytics', icon: BarChart3, features: ['Platform', 'Profitability', 'Orders', 'Referrals'] },
    ]
  }
];

const accentMap = {
  teal: { text: 'text-teal-700', textLight: 'text-teal-600', bg: 'bg-teal-50', border: 'border-teal-200', badgeBg: 'bg-teal-50', badgeText: 'text-teal-600', hoverBg: 'hover:bg-teal-50', popBg: 'bg-white', popBorder: 'border-teal-200' },
  rose: { text: 'text-rose-700', textLight: 'text-rose-600', bg: 'bg-rose-50', border: 'border-rose-200', badgeBg: 'bg-rose-50', badgeText: 'text-rose-600', hoverBg: 'hover:bg-rose-50', popBg: 'bg-white', popBorder: 'border-rose-200' },
  blue: { text: 'text-blue-700', textLight: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-200', badgeBg: 'bg-blue-50', badgeText: 'text-blue-600', hoverBg: 'hover:bg-blue-50', popBg: 'bg-white', popBorder: 'border-blue-200' },
  violet: { text: 'text-violet-700', textLight: 'text-violet-600', bg: 'bg-violet-50', border: 'border-violet-200', badgeBg: 'bg-violet-50', badgeText: 'text-violet-600', hoverBg: 'hover:bg-violet-50', popBg: 'bg-white', popBorder: 'border-violet-200' },
};

function FeatureDropdown({ portal, isOpen, onClose, btnRef }) {
  const dropdownRef = useRef(null);
  const colors = accentMap[portal.accent];

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target) && btnRef.current && !btnRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen, onClose, btnRef]);

  if (!isOpen) return null;

  return (
    <div
      ref={dropdownRef}
      className={`absolute left-0 right-0 top-full mt-1 z-50 rounded-xl ${colors.popBg} border ${colors.popBorder} shadow-xl p-3 animate-in fade-in slide-in-from-top-2 duration-200`}
      data-testid={`features-dropdown-${portal.id}`}
    >
      <div className="flex items-center justify-between mb-2.5">
        <span className={`text-xs font-semibold ${colors.text}`}>All Features</span>
        <button onClick={onClose} className="p-0.5 rounded hover:bg-slate-100 transition-colors">
          <X className="w-3.5 h-3.5 text-slate-400" />
        </button>
      </div>
      <div className="grid grid-cols-2 gap-2">
        {portal.featureGroups.map((group, i) => {
          const GIcon = group.icon;
          return (
            <div key={`el-${i}`} className={`${colors.bg} rounded-lg p-2 border ${colors.border}`}>
              <div className="flex items-center gap-1.5 mb-1">
                <GIcon className={`w-3 h-3 ${colors.textLight}`} />
                <span className="text-[11px] font-semibold text-slate-700">{group.category}</span>
              </div>
              {group.features.map((f, j) => (
                <div key={j} className="text-[10px] text-slate-500 leading-relaxed pl-4">{f}</div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PortalCard({ portal, freeTrialOffer }) {
  const navigate = useNavigate();
  const [showFeatures, setShowFeatures] = useState(false);
  const btnRef = useRef(null);
  const IconComponent = portal.icon;
  const colors = accentMap[portal.accent];

  const formatDuration = (days) => {
    if (days === 7) return '1 Week';
    if (days === 14) return '2 Weeks';
    if (days === 30) return '1 Month';
    if (days === 60) return '2 Months';
    if (days === 90) return '3 Months';
    if (days === 180) return '6 Months';
    if (days === 365) return '1 Year';
    return `${days} Days`;
  };

  return (
    <Card
      className={`relative bg-white border ${portal.borderClass} transition-all duration-300 group shadow-sm hover:shadow-lg`}
      data-testid={`portal-${portal.id}`}
    >
      <div className={`h-1 rounded-t-lg bg-gradient-to-r ${portal.bgGradient}`} />

      <CardContent className="p-2 sm:p-5">
        {/* Header */}
        <div className="flex items-center gap-2 sm:gap-3 mb-1.5 sm:mb-3">
          <div className={`w-8 h-8 sm:w-11 sm:h-11 rounded-lg sm:rounded-xl bg-gradient-to-br ${portal.bgGradient} flex items-center justify-center shadow-md flex-shrink-0`}>
            <IconComponent className="w-3.5 h-3.5 sm:w-5 sm:h-5 text-white" />
          </div>
          <div className="min-w-0">
            <h3 className="text-[13px] sm:text-lg font-bold text-slate-900 leading-tight">{portal.title}</h3>
            <p className={`text-[9px] sm:text-xs ${colors.textLight} font-medium leading-tight`}>{portal.tagline}</p>
          </div>
        </div>

        {/* Description */}
        <p className="hidden sm:block text-sm text-slate-500 leading-snug mb-3">{portal.description}</p>

        {/* Highlight chips */}
        <div className="flex flex-wrap gap-0.5 sm:gap-1.5 mb-1 sm:mb-3">
          {portal.highlights.map((h, i) => {
            const HIcon = h.icon;
            return (
              <span key={`sp-${i}`} className={`inline-flex items-center gap-0.5 px-1 sm:px-2 py-px sm:py-0.5 rounded-full ${colors.badgeBg} border ${colors.border}`}>
                <HIcon className={`w-2 h-2 sm:w-3 sm:h-3 ${colors.textLight}`} />
                <span className="text-[8px] sm:text-xs text-slate-600 font-medium">{h.label}</span>
                {h.isNew && <span className={`text-[6px] sm:text-[8px] font-bold ${colors.badgeText}`}>NEW</span>}
              </span>
            );
          })}
        </div>

        {/* View Features toggle */}
        <div className="relative">
          <button
            ref={btnRef}
            onClick={() => setShowFeatures(!showFeatures)}
            className={`flex items-center gap-0.5 sm:gap-1 text-[9px] sm:text-xs ${colors.textLight} ${colors.hoverBg} px-1 sm:px-2 py-px sm:py-1 rounded-md transition-colors font-medium`}
            data-testid={`view-features-${portal.id}`}
          >
            <ChevronDown className={`w-2 h-2 sm:w-3 sm:h-3 transition-transform ${showFeatures ? 'rotate-180' : ''}`} />
            {showFeatures ? 'Hide' : 'Features'}
          </button>
          <FeatureDropdown portal={portal} isOpen={showFeatures} onClose={() => setShowFeatures(false)} btnRef={btnRef} />
        </div>

        {/* CTA */}
        <div className="mt-1.5 sm:mt-3 pt-1.5 sm:pt-3 border-t border-slate-100">
          <div className="hidden sm:block mb-2.5">
            <p className="text-[10px] text-slate-400 uppercase tracking-widest font-medium">Demo</p>
            <p className="text-xs text-slate-600 font-mono truncate">{portal.demo}</p>
          </div>

          {/* Free Trial button for Doctor portal */}
          {portal.id === 'doctor' && freeTrialOffer && (
            <Button
              className="w-full h-7 sm:h-9 mb-1.5 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white font-bold text-[10px] sm:text-xs shadow-md shadow-orange-200/50"
              onClick={() => navigate('/doctor/login?action=register&offer=free_trial')}
              data-testid="portal-free-trial-btn"
            >
              <Zap className="w-3 h-3 sm:w-3.5 sm:h-3.5 mr-1" />
              Start Free Trial — {formatDuration(freeTrialOffer.duration_days)}
            </Button>
          )}

          <Button
            className={`w-full h-7 sm:h-10 bg-gradient-to-r ${portal.bgGradient} hover:opacity-90 transition-all font-semibold text-[11px] sm:text-sm shadow-md`}
            onClick={() => navigate(portal.route)}
            data-testid={`enter-${portal.id}`}
          >
            Enter <ArrowRight className="w-2.5 h-2.5 sm:w-3.5 sm:h-3.5 ml-0.5 sm:ml-1" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default function PortalSelectionPage() {
  const [freeTrialOffer, setFreeTrialOffer] = useState(null);

  useEffect(() => {
    // Fetch active free trial offers
    axios.get(`${API_URL}/api/subscriptions/offers/public`).then(res => {
      const offers = res.data.offers || [];
      const trial = offers.find(o => o.offer_type === 'free_trial' && o.is_active);
      if (trial) setFreeTrialOffer(trial);
    }).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-teal-50/30 relative">
      {/* Medical Doodle Pattern Background */}
      <div className="fixed inset-0 pointer-events-none z-0" style={{ opacity: 0.45 }}>
        <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="medical-doodle" x="0" y="0" width="320" height="320" patternUnits="userSpaceOnUse">
              <g fill="none" stroke="#c8d5d4" strokeWidth="1.1" strokeLinecap="round" strokeLinejoin="round">
                {/* Stethoscope */}
                <path d="M25 18c0-4 3-7 7-7s7 3 7 7v8c0 3-2 5-5 5h-4" />
                <circle cx="27" cy="33" r="3" />
                <path d="M39 22v4c0 5-4 9-9 9" />
                <circle cx="39" cy="20" r="2" />
                {/* Heart */}
                <path d="M70 25c-3-6-12-6-12 1 0 8 12 14 12 14s12-6 12-14c0-7-9-7-12-1" />
                {/* Pill capsule */}
                <rect x="100" y="15" width="22" height="12" rx="6" transform="rotate(35 111 21)" />
                <line x1="111" y1="14" x2="111" y2="28" transform="rotate(35 111 21)" />
                {/* Syringe */}
                <rect x="148" y="20" width="6" height="20" rx="1" />
                <line x1="151" y1="40" x2="151" y2="48" />
                <line x1="148" y1="26" x2="144" y2="26" />
                <line x1="148" y1="32" x2="144" y2="32" />
                <path d="M148 16h6v4h-6z" />
                {/* Medical cross */}
                <path d="M200 15h8v8h8v8h-8v8h-8v-8h-8v-8h8z" />
                {/* Thermometer */}
                <rect x="240" y="10" width="7" height="28" rx="3.5" />
                <circle cx="243.5" cy="42" r="5" />
                <line x1="243.5" y1="25" x2="243.5" y2="38" />
                {/* Clipboard */}
                <rect x="278" y="18" width="18" height="24" rx="2" />
                <rect x="283" y="14" width="8" height="6" rx="1" />
                <line x1="282" y1="28" x2="292" y2="28" />
                <line x1="282" y1="33" x2="290" y2="33" />
                <line x1="282" y1="38" x2="288" y2="38" />
                {/* DNA double helix */}
                <path d="M20 65c8 4 16-4 24 0s16-4 24 0" />
                <path d="M20 75c8-4 16 4 24 0s16 4 24 0" />
                <line x1="28" y1="67" x2="28" y2="73" />
                <line x1="44" y1="63" x2="44" y2="77" />
                <line x1="60" y1="67" x2="60" y2="73" />
                {/* Test tube */}
                <path d="M90 60v22c0 4 3 7 6 7s6-3 6-7V60" />
                <line x1="88" y1="60" x2="104" y2="60" />
                <path d="M93 75h6" />
                {/* Microscope */}
                <circle cx="140" cy="62" r="5" />
                <line x1="140" y1="67" x2="140" y2="82" />
                <path d="M133 82h14" />
                <path d="M145 62l6-8" />
                <circle cx="153" cy="52" r="3" />
                {/* Bandaid */}
                <rect x="175" y="60" width="28" height="12" rx="6" transform="rotate(-20 189 66)" />
                <circle cx="185" cy="65" r="1" />
                <circle cx="189" cy="64" r="1" />
                <circle cx="193" cy="63" r="1" />
                {/* Tooth */}
                <path d="M230 60c0-6 4-10 8-10s8 4 8 10c0 4-2 7-3 12-1 4-1 8-1 8h-8s0-4-1-8c-1-5-3-8-3-12z" />
                {/* Eye */}
                <path d="M268 70c6-8 18-8 24 0-6 8-18 8-24 0z" />
                <circle cx="280" cy="70" r="4" />
                <circle cx="280" cy="70" r="1.5" />
                {/* Heartbeat ECG */}
                <polyline points="20,120 30,120 35,108 40,132 45,115 50,125 60,120 70,120" />
                {/* Lungs */}
                <path d="M100 105v20" />
                <path d="M100 110c-3-2-12-1-14 8-1 6 4 10 10 10" />
                <path d="M100 110c3-2 12-1 14 8 1 6-4 10-10 10" />
                {/* Blood drop */}
                <path d="M148 105c-6 8-10 14-10 19 0 6 4 10 10 10s10-4 10-10c0-5-4-11-10-19z" />
                {/* Brain */}
                <path d="M190 115c-3-8 2-14 8-14 3 0 5 2 6 4 1-2 3-4 6-4 6 0 11 6 8 14" />
                <path d="M190 115c0 6 4 12 14 12s14-6 14-12" />
                <line x1="204" y1="103" x2="204" y2="127" />
                {/* Rx symbol */}
                <text x="240" y="125" fontFamily="serif" fontSize="22" fontWeight="bold" fill="none" stroke="#c8d5d4" strokeWidth="1.1">Rx</text>
                {/* Hospital building */}
                <rect x="275" y="108" width="20" height="22" rx="1" />
                <path d="M282 108v-6h6v6" />
                <rect x="280" y="116" width="4" height="4" />
                <rect x="286" y="116" width="4" height="4" />
                <rect x="283" y="124" width="4" height="6" />
                <path d="M284 110h2v2h-2z" />
                {/* Stethoscope small */}
                <circle cx="30" cy="170" r="4" />
                <path d="M30 166v-6c0-3 6-3 6 0" />
                {/* Bone */}
                <path d="M60 158c-2-2-2-6 1-7s6 1 5 4l10 14c2 2 2 6-1 7s-6-1-5-4z" />
                {/* Flask/beaker */}
                <path d="M100 155h12v4l6 16h-24l6-16z" />
                <line x1="103" y1="155" x2="103" y2="150" />
                <line x1="109" y1="155" x2="109" y2="150" />
                <line x1="98" y1="150" x2="114" y2="150" />
                {/* Ambulance */}
                <rect x="140" y="160" width="24" height="14" rx="2" />
                <rect x="164" y="164" width="10" height="10" rx="2" />
                <circle cx="150" cy="177" r="3" />
                <circle cx="170" cy="177" r="3" />
                <path d="M150 163v6M147 166h6" />
                {/* Wheelchair */}
                <circle cx="210" cy="175" r="6" />
                <path d="M212 160v10h8l3 5" />
                <path d="M208 160h8" />
                {/* Ear */}
                <path d="M250 155c8-3 14 3 12 10-1 4-4 6-4 10 0 3-2 5-5 5" />
                <path d="M256 163c-2 1-3 3-2 5" />
                {/* Mask */}
                <path d="M280 158c0 0 6 2 10 2s10-2 10-2v10c0 4-4 8-10 8s-10-4-10-8z" />
                <line x1="280" y1="158" x2="276" y2="156" />
                <line x1="300" y1="158" x2="304" y2="156" />
                {/* Dots scattered */}
                <circle cx="15" cy="48" r="1.2" fill="#d4dedd" stroke="none" />
                <circle cx="85" cy="45" r="1.2" fill="#d4dedd" stroke="none" />
                <circle cx="135" cy="50" r="1" fill="#d4dedd" stroke="none" />
                <circle cx="170" cy="48" r="1.2" fill="#d4dedd" stroke="none" />
                <circle cx="225" cy="42" r="1" fill="#d4dedd" stroke="none" />
                <circle cx="260" cy="50" r="1.2" fill="#d4dedd" stroke="none" />
                <circle cx="310" cy="45" r="1" fill="#d4dedd" stroke="none" />
                <circle cx="50" cy="95" r="1" fill="#d4dedd" stroke="none" />
                <circle cx="120" cy="98" r="1.2" fill="#d4dedd" stroke="none" />
                <circle cx="165" cy="92" r="1" fill="#d4dedd" stroke="none" />
                <circle cx="220" cy="96" r="1.2" fill="#d4dedd" stroke="none" />
                <circle cx="265" cy="100" r="1" fill="#d4dedd" stroke="none" />
                <circle cx="40" cy="145" r="1.2" fill="#d4dedd" stroke="none" />
                <circle cx="130" cy="142" r="1" fill="#d4dedd" stroke="none" />
                <circle cx="195" cy="148" r="1.2" fill="#d4dedd" stroke="none" />
                <circle cx="240" cy="142" r="1" fill="#d4dedd" stroke="none" />
                <circle cx="310" cy="148" r="1.2" fill="#d4dedd" stroke="none" />
                {/* Small crosses */}
                <path d="M55 52v4M53 54h4" />
                <path d="M175 95v4M173 97h4" />
                <path d="M305 98v4M303 100h4" />
                <path d="M95 148v4M93 150h4" />
                <path d="M235 152v4M233 154h4" />
                {/* Small stars */}
                <path d="M125 48l1.5 3 3 .5-2 2 .5 3-3-1.5-3 1.5.5-3-2-2 3-.5z" />
                <path d="M215 95l1.5 3 3 .5-2 2 .5 3-3-1.5-3 1.5.5-3-2-2 3-.5z" />
                <path d="M70 142l1.5 3 3 .5-2 2 .5 3-3-1.5-3 1.5.5-3-2-2 3-.5z" />
                {/* Row 4 - more medical items */}
                {/* Dropper */}
                <path d="M25 205l4 4v12l-4 4h-2l-4-4v-12l4-4z" />
                <path d="M22 202c0-3 4-3 4 0" />
                {/* Mortar and pestle */}
                <path d="M60 215c0 6 5 10 12 10s12-4 12-10z" />
                <path d="M58 215h28" />
                <line x1="72" y1="215" x2="80" y2="200" />
                {/* Stool sample cup */}
                <path d="M105 205h14v4l-2 14h-10l-2-14z" />
                <line x1="105" y1="205" x2="119" y2="205" />
                {/* IV drip bag */}
                <path d="M150 198h12v16h-12z" />
                <line x1="156" y1="214" x2="156" y2="226" />
                <path d="M153 226h6" />
                <circle cx="156" cy="220" r="1.5" />
                {/* Scales */}
                <line x1="200" y1="200" x2="200" y2="225" />
                <line x1="190" y1="225" x2="210" y2="225" />
                <line x1="188" y1="200" x2="212" y2="200" />
                <path d="M188 200l-3 8h10z" />
                <path d="M212 200l-3 8h10z" />
                {/* Calendar */}
                <rect x="238" y="200" width="18" height="18" rx="2" />
                <line x1="238" y1="207" x2="256" y2="207" />
                <line x1="243" y1="197" x2="243" y2="203" />
                <line x1="251" y1="197" x2="251" y2="203" />
                <circle cx="244" cy="212" r="1.5" />
                <circle cx="250" cy="212" r="1.5" />
                {/* Phone/app */}
                <rect x="280" y="198" width="14" height="22" rx="2" />
                <line x1="284" y1="200" x2="290" y2="200" />
                <circle cx="287" cy="216" r="1.5" />
                {/* More dots row 4 */}
                <circle cx="45" cy="195" r="1" fill="#d4dedd" stroke="none" />
                <circle cx="92" cy="198" r="1.2" fill="#d4dedd" stroke="none" />
                <circle cx="135" cy="195" r="1" fill="#d4dedd" stroke="none" />
                <circle cx="178" cy="198" r="1.2" fill="#d4dedd" stroke="none" />
                <circle cx="225" cy="195" r="1" fill="#d4dedd" stroke="none" />
                <circle cx="268" cy="198" r="1.2" fill="#d4dedd" stroke="none" />
                <circle cx="310" cy="200" r="1" fill="#d4dedd" stroke="none" />
                {/* Row 5 */}
                {/* Virus */}
                <circle cx="30" cy="260" r="7" />
                <line x1="30" y1="251" x2="30" y2="248" />
                <line x1="30" y1="269" x2="30" y2="272" />
                <line x1="21" y1="260" x2="18" y2="260" />
                <line x1="39" y1="260" x2="42" y2="260" />
                <circle cx="30" cy="248" r="1.5" />
                <circle cx="30" cy="272" r="1.5" />
                <circle cx="18" cy="260" r="1.5" />
                <circle cx="42" cy="260" r="1.5" />
                {/* Hand sanitizer */}
                <rect x="68" y="252" width="12" height="18" rx="2" />
                <path d="M71 248h6v4h-6z" />
                <path d="M74" y="246" />
                <line x1="74" y1="248" x2="74" y2="244" />
                <line x1="72" y1="244" x2="77" y2="244" />
                {/* Kidney */}
                <path d="M115 250c6-4 14 0 14 10s-8 16-14 12c-3-2-2-8 0-10s-6-8 0-12z" />
                {/* Gloves */}
                <path d="M158 255c0-3 2-5 3-5s3 2 3 5v10h-6z" />
                <path d="M152 258c0-3 2-5 3-5s3 2 3 5v7h-6z" />
                <path d="M164 258c0-3 2-5 3-5s3 2 3 5v7h-6z" />
                <path d="M150 265h22v6h-22z" />
                {/* Magnet */}
                <path d="M200 250c0-8 6-14 14-14s14 6 14 14" />
                <rect x="196" y="250" width="8" height="10" />
                <rect x="224" y="250" width="8" height="10" />
                {/* Checklist */}
                <rect x="252" y="248" width="16" height="22" rx="1" />
                <path d="M255 254l2 2 4-4" />
                <line x1="263" y1="254" x2="266" y2="254" />
                <path d="M255 261l2 2 4-4" />
                <line x1="263" y1="261" x2="266" y2="261" />
                {/* Molecule */}
                <circle cx="298" cy="255" r="4" />
                <circle cx="310" cy="248" r="3" />
                <circle cx="312" cy="265" r="3" />
                <line x1="301" y1="253" x2="308" y2="249" />
                <line x1="301" y1="258" x2="310" y2="263" />
                {/* Row 5 dots */}
                <circle cx="52" cy="248" r="1" fill="#d4dedd" stroke="none" />
                <circle cx="100" cy="250" r="1.2" fill="#d4dedd" stroke="none" />
                <circle cx="142" cy="248" r="1" fill="#d4dedd" stroke="none" />
                <circle cx="185" cy="252" r="1.2" fill="#d4dedd" stroke="none" />
                <circle cx="240" cy="248" r="1" fill="#d4dedd" stroke="none" />
                <circle cx="285" cy="252" r="1.2" fill="#d4dedd" stroke="none" />
                {/* Row 5 small crosses */}
                <path d="M88 275v4M86 277h4" />
                <path d="M182 280v4M180 282h4" />
                {/* Row 6 */}
                {/* Spine */}
                <path d="M25 295c3 0 5 2 5 4s-2 4-5 4-5-2-5-4 2-4 5-4z" />
                <path d="M25 305c3 0 4 2 4 3s-1 3-4 3-4-2-4-3 1-3 4-3z" />
                <path d="M25 313c2 0 3 1 3 3s-1 3-3 3-3-1-3-3 1-3 3-3z" />
                {/* Shield/protection */}
                <path d="M65 290l10-4 10 4v10c0 6-5 10-10 12-5-2-10-6-10-12z" />
                <path d="M72 300l3 3 5-6" />
                {/* Tablet/computer */}
                <rect x="105" y="290" width="20" height="14" rx="2" />
                <line x1="110" y1="308" x2="120" y2="308" />
                <line x1="115" y1="304" x2="115" y2="308" />
                {/* Clock */}
                <circle cx="162" cy="300" r="10" />
                <line x1="162" y1="300" x2="162" y2="293" />
                <line x1="162" y1="300" x2="168" y2="300" />
                {/* Wheelchair */}
                <circle cx="210" cy="308" r="6" />
                <path d="M212 292v12h6l2 4" />
                <line x1="208" y1="292" x2="216" y2="292" />
                {/* Plaster/bandaid */}
                <rect x="245" y="290" width="22" height="10" rx="5" transform="rotate(25 256 295)" />
                <line x1="256" y1="289" x2="256" y2="301" transform="rotate(25 256 295)" />
                {/* Wallet/insurance */}
                <rect x="290" y="290" width="18" height="14" rx="2" />
                <line x1="290" y1="296" x2="308" y2="296" />
                <circle cx="302" cy="301" r="2" />
                {/* Row 6 dots */}
                <circle cx="45" cy="295" r="1.2" fill="#d4dedd" stroke="none" />
                <circle cx="90" cy="292" r="1" fill="#d4dedd" stroke="none" />
                <circle cx="140" cy="295" r="1.2" fill="#d4dedd" stroke="none" />
                <circle cx="190" cy="292" r="1" fill="#d4dedd" stroke="none" />
                <circle cx="230" cy="298" r="1.2" fill="#d4dedd" stroke="none" />
                <circle cx="278" cy="292" r="1" fill="#d4dedd" stroke="none" />
              </g>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#medical-doodle)" />
        </svg>
      </div>

      {/* Header */}
      <header className="relative z-10 py-2 sm:py-4 px-3 sm:px-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          {/* Logo - dark version for light bg */}
          <div className="flex items-center gap-2">
            <svg width="32" height="32" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="sm:w-10 sm:h-10">
              <circle cx="60" cy="60" r="45" fill="#0d9488" fillOpacity="0.1" stroke="#0d9488" strokeWidth="4" />
              <text x="48" y="82" fontFamily="Inter" fontSize="64" fontWeight="800" fill="#0f172a">1</text>
              <path d="M78 40V54M71 47H85" stroke="#f97316" strokeWidth="5" strokeLinecap="round" />
            </svg>
            <div>
              <span className="text-lg sm:text-2xl font-extrabold text-slate-900" style={{ fontFamily: 'Manrope, Inter, sans-serif' }}>1Clin</span>
              <p className="text-[7px] sm:text-[9px] text-slate-400 font-medium tracking-wider leading-none">HEALTHCARE SIMPLIFIED</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="border-violet-200 text-violet-700 hover:bg-violet-50 text-xs"
              onClick={() => window.location.href = 'https://immuski.com'} data-testid="visit-immuski-btn">
              <ArrowRight className="w-3 h-3 mr-1" /> Immuski
            </Button>
            <DownloadAppButton
              variant="outline"
              size="sm"
              className="border-teal-300 text-teal-700 hover:bg-teal-50 text-xs"
            />
            <div className="flex items-center gap-1.5 bg-amber-50 px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-full border border-amber-200">
              <Sparkles className="w-3 h-3 text-amber-500" />
              <span className="text-[9px] sm:text-xs text-amber-700 font-medium">AI Powered</span>
            </div>
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="relative z-10 px-3 sm:px-4 pb-3 sm:pb-6">
        <div className="max-w-6xl mx-auto">
          {/* Hero */}
          <div className="text-center py-1.5 sm:py-8">
            <h1 className="text-lg sm:text-4xl md:text-5xl font-bold text-slate-900 mb-0.5 sm:mb-1 tracking-tight">
              Welcome to{' '}
              <span className="bg-gradient-to-r from-teal-600 via-blue-600 to-violet-600 bg-clip-text text-transparent">1Clin</span>
            </h1>
            <p className="text-[10px] sm:text-base text-slate-500">Less paperwork, more pulse checks!</p>
          </div>

          {/* Free Trial Banner - prominent, above cards */}
          {freeTrialOffer && (
            <div className="mb-4 sm:mb-6 max-w-2xl mx-auto" data-testid="portal-trial-banner">
              <div className="relative overflow-hidden rounded-xl bg-gradient-to-r from-orange-500 via-amber-500 to-yellow-400 p-[1.5px]">
                <div className="rounded-[10px] bg-gradient-to-r from-orange-50 to-amber-50 px-4 py-3 sm:py-4 flex flex-col sm:flex-row items-center gap-3 sm:gap-4">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center flex-shrink-0">
                    <Zap className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1 text-center sm:text-left">
                    <h3 className="font-bold text-slate-900 text-sm sm:text-base">{freeTrialOffer.title}</h3>
                    <p className="text-xs text-slate-600">
                      {freeTrialOffer.description || 'Get all premium features absolutely free. No payment required!'}
                    </p>
                  </div>
                  <Button
                    className="bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700 text-white font-bold px-5 h-10 shadow-lg shadow-orange-300/40 text-xs sm:text-sm"
                    onClick={() => window.location.href = '/doctor/login?action=register&offer=free_trial'}
                    data-testid="portal-banner-trial-btn"
                  >
                    <Sparkles className="w-4 h-4 mr-1.5" />
                    Sign Up Free
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4">
            {portals.map((p) => <PortalCard key={p.id} portal={p} freeTrialOffer={p.id === 'doctor' ? freeTrialOffer : null} />)}
          </div>

          {/* Stats */}
          <div className="mt-2 sm:mt-10 flex sm:grid sm:grid-cols-4 gap-1.5 sm:gap-4 sm:max-w-2xl sm:mx-auto justify-between">
            {[
              { value: '60+', label: 'Features', icon: Sparkles },
              { value: '4', label: 'Portals', icon: Shield },
              { value: '35+', label: 'Specialties', icon: Heart },
              { value: '24/7', label: 'Support', icon: MessageSquare },
            ].map((stat, i) => (
              <div key={`el-${i}`} className="text-center bg-white rounded-lg py-1.5 sm:py-2.5 px-2 sm:px-2 border border-slate-200 shadow-sm flex-1">
                <stat.icon className="w-3 h-3 sm:w-4 sm:h-4 mx-auto mb-0.5 sm:mb-1 text-teal-600" />
                <p className="text-sm sm:text-2xl font-bold text-slate-900 leading-tight">{stat.value}</p>
                <p className="text-[8px] sm:text-xs text-slate-500">{stat.label}</p>
              </div>
            ))}
          </div>

          {/* Footer badges */}
          <div className="mt-1.5 sm:mt-8 flex items-center justify-center gap-1.5 sm:gap-4">
            {[
              { icon: Shield, label: 'ABDM Ready', color: 'text-green-600' },
              { icon: Shield, label: 'HIPAA', color: 'text-blue-600' },
              { icon: Heart, label: 'Made in India', color: 'text-orange-500' },
            ].map((badge, i) => (
              <span key={`sp-${i}`} className="flex items-center gap-0.5 sm:gap-1 bg-white px-1.5 sm:px-2.5 py-0.5 sm:py-1 rounded-full text-[8px] sm:text-xs text-slate-500 border border-slate-200">
                <badge.icon className={`w-2.5 h-2.5 sm:w-3 sm:h-3 ${badge.color}`} />
                {badge.label}
              </span>
            ))}
          </div>

          <p className="mt-2 sm:mt-4 text-center text-[9px] sm:text-xs text-slate-400">
            By signing up, you agree with our{' '}
            <Link to="/terms" className="text-teal-600 hover:text-teal-700 underline underline-offset-2" data-testid="terms-link">Terms and Conditions</Link>
            {' '}and{' '}
            <Link to="/privacy" className="text-teal-600 hover:text-teal-700 underline underline-offset-2" data-testid="privacy-link">Privacy Policy</Link>.
          </p>
        </div>
      </main>

      {/* Proudly Made In India badge - portal page only */}
      <div className="fixed bottom-4 right-4 z-50 inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-gradient-to-r from-slate-900 to-slate-800 border border-white/10 shadow-lg">
        <svg width="18" height="13" viewBox="0 0 18 13" fill="none"><rect width="18" height="4.33" fill="#FF9933"/><rect y="4.33" width="18" height="4.33" fill="#FFF"/><rect y="8.67" width="18" height="4.33" fill="#138808"/><circle cx="9" cy="6.5" r="1.8" fill="none" stroke="#000080" strokeWidth="0.4"/></svg>
        <span className="text-[11px] font-semibold whitespace-nowrap"><span className="text-orange-400">Proudly</span> <span className="text-white">Made In</span> <span className="text-green-500">India</span></span>
      </div>
    </div>
  );
}
